import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { ModalConfirm } from '@components/Modal';
import { useTranslation } from '@hooks';
import { accountActions, alertActions, authActions } from '@actions';
import { teamsService } from '@services/teams';
import { handleApiError } from '@helpers/api';
import TeamEditModal, { FORM_TYPE } from './TeamEditModal';
import TeamsTable from './TeamsTable';

const Container = styled('div')({
    '@media (max-width: 1024px)': {
        overflowY: 'auto',
        overflowX: 'hidden',
        height: 'calc(var(--100vh) - 221px)',
    },
});

const TeamManagement = () => {
    const [teams, setTeams] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [modal, setModal] = useState({ open: false, type: FORM_TYPE.NEW });
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

    const account = useSelector(state => state.account?.account);
    const brands = useSelector(state => state.brands?.data || []);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const fetchTeams = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await teamsService.getTeams();
            setTeams(data);
        } catch (error) {
            handleApiError(error, 'alertMessages.loadFail');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTeams();
    }, [fetchTeams]);

    const refreshAfterMutation = useCallback(async () => {
        await fetchTeams();
        dispatch(authActions.getCurrentUser());
        dispatch(accountActions.get());
    }, [fetchTeams, dispatch]);

    const onDeleteTeam = async () => {
        setDeleteModalOpen(false);
        try {
            await teamsService.deleteTeam(selectedTeam.id);
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            await refreshAfterMutation();
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail');
        }
        setSelectedTeam(null);
    };

    const onDuplicateTeam = async team => {
        try {
            await teamsService.duplicateTeam(team.id, { name: `${team.name} (${t('common.copy')})` });
            dispatch(alertActions.success(t('alertMessages.createSuccess')));
            await refreshAfterMutation();
        } catch (error) {
            handleApiError(error, 'alertMessages.createFail');
        }
    };

    return (
        <Container>
            <TeamsTable
                teams={teams}
                isLoading={isLoading}
                setSelectedTeam={setSelectedTeam}
                setDeleteModalOpen={setDeleteModalOpen}
                setModal={setModal}
                onDuplicateTeam={onDuplicateTeam}
            />
            {modal.open && (
                <TeamEditModal
                    selectedTeam={selectedTeam}
                    setSelectedTeam={setSelectedTeam}
                    modal={modal}
                    setModal={setModal}
                    brands={brands}
                    account={account}
                    onSaved={refreshAfterMutation}
                />
            )}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => {
                        setDeleteModalOpen(false);
                        setSelectedTeam(null);
                    }}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDeleteTeam}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </Container>
    );
};

export default TeamManagement;
